import React, {
  useEffect,
  useContext,
  createContext,
  useReducer,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

export const recipientContext = createContext();

// Wrap the hook with a provider
export const ProvideRecipient = ({ children }) => {
  const inc = useProvideRecipient();
  // We are providing the object returned by useProvideRecipient as the value of the recipientContext
  return <recipientContext.Provider value={inc}>{children}</recipientContext.Provider>;
};

ProvideRecipient.propTypes = {
  children: PropTypes.element,
};

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useRecipient = () => {
  // The context object will contain anything returned by useProvideRecipient
  const context = useContext(recipientContext);
  if (context === undefined) {
    throw new Error('useRecipient must be used within a ProvideRecipient');
  }
  return context;
};

const initialState = {
  recipient: null,
  id: null,
  version: null,
  stale: true,
  loading: false,
  modifying: false,
  error: null,
  recipients: [],
};

export const ACTIONS = Object.freeze({
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  SET_MODIFYING: 'SET_MODIFYING',
  SET_STALE: 'SET_STALE',
  SET_VERSION: 'SET_VERSION',
  SET_RECIPIENT: 'SET_RECIPIENT',
  UPDATE_RECIPIENT: 'UPDATE_RECIPIENT',
  DELETE_RECIPIENT: 'DELETE_RECIPIENT',
  SET_ID: 'SET_ID',
  DELETE: 'DELETE',
  RESET: 'RESET',
});


const recipientReducer = (nextState, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return initialState;
    case ACTIONS.FETCH_INIT:
      return {
        ...nextState,
        loading: true,
        error: null,
      };
    case ACTIONS.FETCH_SUCCESS:
      if (action.payload.deleted) {
        return {
          ...nextState,
          loading: false,
          error: 'Individual Recipient has been deleted',
          stale: false,
        };
      }

      return {
        ...nextState,
        loading: false,
        recipient: action.payload,
        recipients: action.payload?.recipients || [],
        stale: false,
      };
    case ACTIONS.FETCH_ERROR:
      return {
        ...nextState,
        loading: false,
        recipient: null,
        stale: false,
        error: 'Individual Recipient not found',
      };
    case ACTIONS.UPDATE_RECIPIENT:
    case ACTIONS.DELETE_RECIPIENT:
      return {
        ...nextState,
        // recipient: action.payload,
        version: action.payload.version,
        stale: true,
      };
    case ACTIONS.DELETED:
      return {
        ...nextState,
        recipient: null,
        stale: true,
      };
    case ACTIONS.FETCH_FAILURE:
      return {
        ...nextState,
        loading: false,
        error: action.payload,
      };
    case ACTIONS.SET_ID: {
      // let stale = nextState.id === action.payload ? false : true;// TODO: not sure what this is doing so hard coding for now
      let stale = true;
      return {
        ...nextState,
        stale,
        id: action.payload,
      };
    }
    case ACTIONS.SET_VERSION:
      return {
        ...nextState,
        version: action.payload,
      };
    case ACTIONS.SET_MODIFYING:
      return {
        ...nextState,
        modifying: action.payload,
      };
    case ACTIONS.SET_RECIPIENT:
      return {
        ...nextState,
        recipients: action.payload,
      };
    case ACTIONS.SET_STALE:
      return {
        ...nextState,
        stale: action.payload,
      };
    default:
      throw new Error(`recipientReducer this action is not supported: ${action.type}`);
  }
};

const useProvideRecipient = () => {
  const controller = useRef(null);
  //recipient state as a reducer
  const [recipientState, dispatch] = useReducer(recipientReducer, initialState);

  useEffect(() => {
    const {id, stale} = recipientState;
    if (stale && id) {
      dispatch({type: ACTIONS.FETCH_INIT})
      let mounted = true;
      controller.current = new AbortController();
      const getRecipient = () => {
        axios.get(`/api/recipient/get?id=${id}`, { signal: controller.current.signal }).then(res => {
          if (mounted) {
            dispatch({type: ACTIONS.FETCH_SUCCESS, payload: res.data});
          }
        }
        ).catch(error => {
          if (error.name !== 'CanceledError') {
            dispatch({type: ACTIONS.FETCH_ERROR, payload: error});
          }
        });
      }

      getRecipient();

      // Cleanup on unmount
      return () => {
        controller.current.abort();
        mounted = false;
      }
    }
  }, [recipientState.stale, recipientState.id]);

  return {
    unmount: () => {
      dispatch({type: ACTIONS.RESET});
      if (recipientState.loading) {
        controller.current.abort();
      }
    },
    dispatch,
    recipientState,
  }
};
